import React, { useState } from 'react';
import { RCButton, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { NudgeExploreIcon, TrophyIcon } from '@reverbdotcom/cadence/icons/react';
import { Listing, NextBestAction } from '@reverbdotcom/commons/src/gql/graphql';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';

import BumpModalV2WithController from './footer_buttons/BumpButtonV2';
import { bumpRatePercentToDecimal } from '../bump/bump_helpers';
import {
  buildClickedEvent,
  CTA_TYPE,
  INSIGHT,
  trackClicked,
  useTrackingContext,
  useTrackImpression,
} from './sellerRecommendationTracking';

interface Props {
  listing: Listing;
  nextBestAction: NextBestAction;
}

export default function NBAPromoteWithBumpPresentment({ listing, nextBestAction }: Props) {
  const [showBumpModal, setShowBumpModal] = useState(false);
  const trackingContext = useTrackingContext();

  const featuredWinner = !!listing.featuredListingInsight?.featuredWinner;
  const insight = featuredWinner ? INSIGHT.TOP_PICK_WINNER : INSIGHT.TOP_PICK_ELIGIBLE;

  const recommendedRate = listing.featuredListingInsight?.recommendedBumpRate;
  const bumpRate = listing.bumpRate?.rate || 0;

  useTrackImpression(insight, listing.id, { recommendedBumpRate: recommendedRate ?? undefined });

  return (
    <>
      <div className="seller-listing-insight-presentment">
        <span className={featuredWinner ?
          'seller-listing-insight-presentment__top-pick-winner' :
          'seller-listing-insight-presentment__top-pick-eligible'}
        >
          <RCTextWithIcon
            icon={featuredWinner ? <TrophyIcon /> : <NudgeExploreIcon />}
            placement="left"
            size="large"
          >
            <RCTextUtility size="350" weight="bold">
              <SanitizedRender html={nextBestAction.title ?? ''} />
            </RCTextUtility>
          </RCTextWithIcon>
        </span>
        <RCTextUtility size="350">
          <SanitizedRender html={nextBestAction.subtitle ?? ''} />
        </RCTextUtility>

        {!!nextBestAction.footerText && (
          <RCTextUtility size="350">
            {nextBestAction.footerUrl ? (
              <CoreLink
                to={nextBestAction.footerUrl}
                className="text-link"
                target="_blank"
                rel="noreferrer"
                clickEvent={buildClickedEvent(insight, listing.id, CTA_TYPE.VIEW_PRODUCT_PAGE, trackingContext)}
              >
                <SanitizedRender html={nextBestAction.footerText} />
              </CoreLink>
            ) : (
              <SanitizedRender html={nextBestAction.footerText} />
            )}
          </RCTextUtility>
        )}

        {!!nextBestAction.ctaText && (
          <RCButton
            variant="outlined"
            size="small"
            onClick={() => {
              trackClicked(insight, listing.id, CTA_TYPE.PROMOTE_WITH_BUMP, trackingContext);
              setShowBumpModal(true);
            }}
            fullWidth
          >
            {nextBestAction.ctaText}
          </RCButton>
        )}
      </div>

      {!!nextBestAction.ctaText && (
        <BumpModalV2WithController
          listingId={listing.id}
          bid={bumpRatePercentToDecimal(bumpRate)}
          showModal={showBumpModal}
          setShowModal={setShowBumpModal}
          listing={listing}
          sourceInsight={insight}
        />
      )}
    </>
  );
}
